import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { QuillModule } from 'ngx-quill';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatListModule } from '@angular/material/list';
import { MatTreeModule } from '@angular/material/tree';
import { MatRadioModule } from '@angular/material/radio';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatStepperModule } from '@angular/material/stepper';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgxPaginationModule } from 'ngx-pagination';
import { CommonModule } from '@angular/common';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ModalModule } from 'ngx-bootstrap/modal';
import { DatePipe } from '@angular/common';
import { AdminService } from './admin.service';
import { FeatureComponent, PreviewFeatureDialogComponent } from './feature/feature.component';
import { PageTreeComponent } from './page-tree/page-tree.component';
import {
  StepComponent, EditTileDialogComponent, PhoneCompatibilityDialogComponent,
  DisclaimerDialogComponent, PreviewDialogComponent, VideoUrlDialogComponent
} from './step/step.component';
import { HomeComponent } from './home/home.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '../shared/shared.module';
import { AdminComponent } from './admin.component';
import { ManageVehicleComponent } from './manage-vehicle/manage-vehicle.component';
import { AddVehicleComponent, AddVehicleDialogComponent } from './add-vehicle/add-vehicle.component';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { CanDeactivateGuard } from '../shared/services/can-deactivate-guard.service';
import { environment } from '../../environments/environment';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ManageDocumentsComponent, AddDocumentDialogComponent } from './manage-documents/manage-documents.component';
import { ManageDisclaimerComponent, AddDisclaimerDialogComponent } from './manage-disclaimer/manage-disclaimer.component';
import { ManageVideosComponent, AddVideosDialogComponent } from './manage-videos/manage-videos.component';
import { ManageResourcesComponent, AddResourcesDialogComponent } from './manage-resources/manage-resources.component';
import { PublishFeatureComponent } from './publish-feature/publish-feature.component';
import { DraftFeatureComponent } from './publish-feature/draft-feature/draft-feature.component';
import { PreviewFeatureFlowDialogComponent } from '../shared/components/preview-feature-flow-dialog/preview-feature-flow-dialog.component';
import { ManageErrorGuidesComponent } from './manage-error-guides/manage-error-guides.component';
import { AddErrorGuidesComponent } from './manage-error-guides/add-error-guides/add-error-guides.component';
import { ManageMappingComponent } from './manage-mapping/manage-mapping.component';
import { ManageCvsMappingComponent } from './manage-cvs-mapping/manage-cvs-mapping.component';
import { AddCvsMappingComponent } from './manage-cvs-mapping/add-cvs-mapping/add-cvs-mapping.component';
import { ManageBannerComponent, PreviewBannerDialogComponent } from './manage-banner/manage-banner.component';
import { AddBannerComponent } from './manage-banner/add-banner/add-banner.component';
import { ManageCategoryMappingComponent } from './manage-category-mapping/manage-category-mapping.component';
import { DraftBannerListComponent } from './manage-banner/draft-banner-list/draft-banner-list.component';
import { DialogFeatureDraftComponent } from './dialog-feature-draft/dialog-feature-draft.component';
import { BadgeManagementComponent } from './badge-management/badge-management.component';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { MsalTokenInterceptorService } from '../shared/services/msal-token-interceptor.service';
import { Utilities } from '../shared/utilities';
import { ExpirationDialogComponent } from './expiration-dialog/expiration-dialog.component';  

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1; // Remove this line to use Angular Universal

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: Utilities.getAppId(),  
      authority: `https://login.microsoftonline.com/${Utilities.getTentantId()}`, 
    
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11. Remove this line to use Angular Universal
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: true
      }
    }
  });
}


export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['User.Read']); 

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { 
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['User.Read']
    },
    loginFailedRoute: '/login-failed'
  };
}

const routes: Routes = [{
  path: 'admin',
  component: AdminComponent,
  runGuardsAndResolvers: 'always',
  canActivate:[MsalGuard],
  children: [{
    path: '',
    component: HomeComponent

  }, {
    path: 'feature',
    component: FeatureComponent,
    canDeactivate: [CanDeactivateGuard],

  },
  {
    path: 'flow',
    component: StepComponent,
    canDeactivate: [CanDeactivateGuard]
  },
  {
    path: 'vehicle',
    component: ManageVehicleComponent
  },
  {
    path: 'addVehicle',
    component: AddVehicleComponent,
    canDeactivate: [CanDeactivateGuard]
  },
  {
    path: 'manage-documents',
    component: ManageDocumentsComponent
  },
  {
    path: 'manage-disclaimer',
    component: ManageDisclaimerComponent
  },
  {
    path: 'manage-videos',
    component: ManageVideosComponent
  },
  {
    path: 'manage-resources',
    component: ManageResourcesComponent
  },
  {
    path: 'publish',
    component: PublishFeatureComponent
  },
  {
    path: 'manage-message-guide',
    component: ManageErrorGuidesComponent
  },
  {
    path: 'add-message-guide',
    component: AddErrorGuidesComponent,
    canDeactivate: [CanDeactivateGuard]
  },
  {
    path: 'manage-mapping',
    component: ManageMappingComponent,
    canDeactivate: [CanDeactivateGuard]
  },
  {
    path: 'manage-cvs-mapping',
    component: ManageCvsMappingComponent,
    canDeactivate: [CanDeactivateGuard]
  },
  {
    path: 'add-cvs-mapping',
    component: AddCvsMappingComponent,
    canDeactivate: [CanDeactivateGuard]
  },
  {
    path: 'manage-banners',
    component: ManageBannerComponent,
    canDeactivate: [CanDeactivateGuard]
  },
  {
    path: 'add-banner',
    component: AddBannerComponent,
    canDeactivate: [CanDeactivateGuard]
  },
  {
    path: 'manage-category-mapping',
    component: ManageCategoryMappingComponent,
    canDeactivate: [CanDeactivateGuard]
  },
  {
    path: 'badge-management',
    component: BadgeManagementComponent,
    canDeactivate: [CanDeactivateGuard]
  }
  ]
}
];

@NgModule({
  declarations: [
    AdminComponent, FeatureComponent, PageTreeComponent,
    StepComponent, HomeComponent, ManageVehicleComponent, AddVehicleDialogComponent,
    AddVehicleComponent, EditTileDialogComponent, PhoneCompatibilityDialogComponent, ManageDocumentsComponent,
    DisclaimerDialogComponent, ManageDisclaimerComponent,
    AddDisclaimerDialogComponent, AddDocumentDialogComponent, ManageResourcesComponent,
    AddResourcesDialogComponent, AddVideosDialogComponent, ManageVideosComponent,
    PreviewDialogComponent, PreviewFeatureDialogComponent, PublishFeatureComponent, DraftFeatureComponent,
    PreviewFeatureFlowDialogComponent, ManageErrorGuidesComponent,
    AddErrorGuidesComponent,
    ManageMappingComponent,
    ManageCvsMappingComponent,
    AddCvsMappingComponent, VideoUrlDialogComponent, ManageBannerComponent, AddBannerComponent,
    PreviewBannerDialogComponent, ManageCategoryMappingComponent, DraftBannerListComponent, DialogFeatureDraftComponent, BadgeManagementComponent,
    ExpirationDialogComponent 
  ],
  imports: [
    AlertModule.forRoot(),
    CommonModule,
    MatCardModule, MatIconModule, MatToolbarModule, MatButtonModule,
    MatFormFieldModule, MatInputModule, MatSidenavModule, MatCheckboxModule, MatNativeDateModule,
    MatListModule, MatTreeModule, MatRadioModule, MatMenuModule,
    MatProgressSpinnerModule, MatTooltipModule, MatSortModule, MatTableModule, MatPaginatorModule,
    MatProgressBarModule, DragDropModule, MatStepperModule, 
    QuillModule.forRoot(),
    PopoverModule.forRoot(),
    ModalModule.forRoot(),
    RouterModule.forChild(routes),
    TranslateModule,
    FormsModule, ReactiveFormsModule, SharedModule,  
    NgMultiSelectDropDownModule.forRoot(),
    MatDialogModule,
    MatSelectModule,
    MatExpansionModule,
    MatButtonToggleModule,
    MatAutocompleteModule,
    MatTabsModule,
    NgxPaginationModule
  ],
  exports:[
    MatFormFieldModule, TranslateModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalTokenInterceptorService,
      multi: true
    },{
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: MAT_DIALOG_DATA, useValue: {} },
    AdminService, DatePipe, CanDeactivateGuard],
  entryComponents: [AddVehicleComponent, AddVehicleDialogComponent, EditTileDialogComponent,
    PhoneCompatibilityDialogComponent, DisclaimerDialogComponent, AddDisclaimerDialogComponent,
    AddDocumentDialogComponent, AddResourcesDialogComponent,
    AddVideosDialogComponent, PreviewFeatureDialogComponent, PreviewDialogComponent,
    PreviewFeatureFlowDialogComponent, VideoUrlDialogComponent, PreviewBannerDialogComponent, DialogFeatureDraftComponent, ExpirationDialogComponent]  // Include the new component here
})
export class AdminModule { }